import {
  Avatar,
  Box,
  Text,
  Button,
  Drawer,
  DrawerBody,
  DrawerFooter,
  DrawerOverlay,
  DrawerContent,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  useMediaQuery,
} from "@chakra-ui/react";
import ProfileView from "./components/ProfileView";
import WallpapersView from "./components/WallpapersView";
import AwardsView from "./components/AwardsView";
import { FullScreenContext } from "../../contexts/FullScreenContext";
import { useCallback, useEffect, useState } from "react";
import { useSettingsContext } from "../../contexts/SettingsContext";
import {FormattedMessage, useIntl} from "react-intl";
import APIconButton from "../../components/APIconButton";

interface ProfileInterface {
  sdk: any;
  sessionData: any;
}

function Profile(props: ProfileInterface) {
  const {sdk, sessionData} = props;
  const intl = useIntl();

  const {
    profileData,
    setProfileData,
    profileOpen,
    setProfileOpen,
    defaultProfileTab,
    avatar,
    setAvatar,
    settingsTabIndex,
    setSettingsTabIndex,
  } =
    useSettingsContext();

  const [isLargeScreen] = useMediaQuery("(min-width: 764px)");
  const [selectedBg, setSelectedBg] = useState<number | string>(0);
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);

  const onSave = async (selectedBackground: number) => {
    setProfileData({
      ...profileData,
      bg: selectedBackground,
    });

    const res = await sdk.getDataManager().saveSettings({
      data: {
        bg: selectedBackground,
      },
    });
  };

  const getProfile = useCallback(async () => {
    const data = await sdk.getDataManager().getUserDoc();
    const settings = await sdk.getDataManager().getSettings();
    if (data || settings) {
      const annualProblemsList = settings?.annualProblemsList;
      const annualExerciseCount = settings?.annualExerciseCount;
      setProfileData({
        username: data.nickname,
        username_fr: data.pseudonymConfig.nickname_fr,
        level: `
          ${intl.formatMessage({
            id: data.level > 6 ? 'secondary-label' : 'primary-label',
            defaultMessage: data.level > 6 ? "Secondaire" : "Primaire"
          })} ${data.level > 6 ? data.level - 6 : data.level}
        `,
        totalSuccessScore: 140,
        successRate: 75,
        finishedexercises: 4,
        bg: settings && settings.bg ? settings.bg : 1,
        annualProblemsList: `${(annualProblemsList) ? annualProblemsList?.length : 0}`,
        annualExerciseCount: `${(annualExerciseCount)?annualExerciseCount:0}`,
        type: data.type
      });
    }

    setSelectedBg(settings && settings.bg ? settings.bg : 1);
  }, [sdk, profileOpen]);

  useEffect(() => {
    if (sdk) {
      setIsLoggedIn(sdk._firebase.isLoggedIn());
      getProfile();
    }
  }, [sdk, getProfile]);

  const setBackground = (bg: number) => {
    setSelectedBg(bg);
    onSave(bg);
  };

  const tabs: { id: string, defaultMessage: string }[] = [
    {id: "profile", defaultMessage: "Profil"},
    {id: "wallpapers", defaultMessage: "Fonds d’écrans"}
  ];

  return (
    <>
      <Drawer
        isOpen={profileOpen}
        onClose={() => setProfileOpen(false)}
        size="xl"
      >
        <DrawerOverlay/>
        <FullScreenContext.Consumer>
          {({fullScreen, toggleFullScreen}) => (
            <DrawerContent
              width={!isLargeScreen ? "85%!important" : "504px!important"}
              borderTopLeftRadius="0!important"
              borderBottomLeftRadius="0!important"
              overflow={"hidden"}
            >
              <DrawerBody padding={0}>
                <Box
                  p={isLargeScreen ? 8 : 6}
                  display="flex"
                  alignItems={"center"}
                  justifyContent={isLoggedIn ? "space-between" : "flex-end"}
                >
                  {isLoggedIn && (
                    <Box display="flex">
                      <Avatar
                        src={
                          avatar && avatar !== "0" ?
                            `avatars/${avatar}.svg` : profileData ? undefined : `avatars/fire.svg`
                        }
                        name={intl.locale === "fr" ? profileData?.username_fr : profileData?.username}
                        width={isLargeScreen ? "4.5rem" : 14}
                        height={isLargeScreen ? "4.5rem" : 14}
                        mr={isLargeScreen ? 3 : 2}
                        size={'lg'}
                      />
                      <Box py={1}>
                        <Text fontSize={isLargeScreen ? 23 : 18} fontWeight={"bold"} lineHeight={1.5}>
                          {profileData?.username}
                        </Text>
                        <Text fontSize={isLargeScreen ? 18 : 14} fontWeight={"normal"} lineHeight={1.5}>
                          {profileData?.type === "student" && profileData?.level}
                          {profileData?.type === "teacher" && (
                            <FormattedMessage id="teacher" defaultMessage="Professionnel(lle)"/>
                          )}
                          {profileData?.type === "parent" && (
                            <FormattedMessage id="parent" defaultMessage="Parent"/>
                          )}
                        </Text>
                      </Box>
                    </Box>
                  )}
                  <APIconButton sx={{bg: "primary", boxShadow: "none"}} onClick={() => setProfileOpen(false)}>
                    <img src={`${process.env.REACT_APP_BASE_URL}/close.png`}/>
                  </APIconButton>
                </Box>
                <Tabs
                  variant="soft-rounded"
                  onChange={(index) => setSettingsTabIndex(index)}
                  defaultIndex={defaultProfileTab}
                >
                  <TabList sx={{px: isLargeScreen ? 5 : 4}}>
                    {tabs.map((tab, index) => (
                      <Button
                        key={index}
                        fontSize={isLargeScreen ? 20 : 16}
                        lineHeight={1.5}
                        fontWeight={500}
                        letterSpacing={"normal"}
                        whiteSpace={"nowrap"}
                        py={isLargeScreen ? 3 : 2}
                        px={isLargeScreen ? 5 : 4}
                        mr={isLargeScreen ? 5 : 4}
                        bg={"#f5f3ef"}
                        color={"black"}
                        role="button"
                        onClick={() => setSettingsTabIndex(index)}
                        style={{
                          height: "auto",
                          borderWidth: "2px 2px 0px 2px",
                          borderStyle: "solid",
                          borderColor: "#F5F3EF",
                          backgroundColor: settingsTabIndex === index ? "#FCFCFA" : "#F5F3EF",
                          borderRadius: "12px 12px 0px 0px",
                        }}
                        sx={{
                          "&:after": {
                            content: '""',
                            display: settingsTabIndex === index ? "block" : "none",
                            bg: "#fcfcfa",
                            height: "2px",
                            position: "absolute",
                            width: "100%",
                            bottom: "-2px"
                          }
                        }}
                      >
                        <FormattedMessage
                          id={tab.id}
                          defaultMessage={tab.defaultMessage}
                        />
                      </Button>
                    ))}
                  </TabList>
                  <div style={{
                    height: "calc(100% - 96px)",
                    margin: isLargeScreen ? "0 1.26rem" : "0 1rem",
                    border: "2px solid #F5F3EF",
                    borderRadius: "0px 16px 16px 16px",
                    background: "#fcfcfa"
                  }}>
                    <div style={{height: "100%"}}>
                      {settingsTabIndex === 0 && (
                        <ProfileView
                          profile={profileData}
                          isLoggedIn={isLoggedIn}
                          sessionData={sessionData}
                        />
                      )}
                      {settingsTabIndex === 1 && (
                        <WallpapersView
                          setBackground={setBackground}
                          background={selectedBg}
                          isLoggedIn={sdk._firebase.isLoggedIn()}
                          sdk={sdk}
                        />
                      )}
                    </div>
                  </div>
                </Tabs>
              </DrawerBody>
            </DrawerContent>
          )}
        </FullScreenContext.Consumer>
      </Drawer>
    </>
  );
}

export default Profile;
